import {BlogItem} from "../../../model/user/blog/Blog";
import React from "react";
import {Link} from "react-router-dom"
import {formatISODateString} from "../../../util/DateUtil";
import AnimatedLink from "../../ui/motion/AnimatedLink";
import {motion} from "framer-motion";

interface BlogPostItemProps {
    blogItem: BlogItem
    isAdmin: boolean
    readonly?: boolean
    isFeatured?: boolean
    index?: number
    largeCard?: boolean
    setArticleToDelete: React.Dispatch<React.SetStateAction<BlogItem | null>>
    setConfirmDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export default function BlogPostItem(props: BlogPostItemProps) {
    const {
        blogItem,
        isAdmin,
        readonly,
        isFeatured = false,
        index = 0,
        largeCard = false,
        setArticleToDelete,
        setConfirmDeleteModalOpen
    } = props;

    const cardVariants = {
        hidden: {
            opacity: 0,
            y: 40
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                delay: (index % 20) * 0.15,
                ease: [0.25, 0.1, 0.25, 1]
            }
        }
    };

    // Featured Article - Full width hero style
    if (isFeatured) {
        return (
            <motion.div
                className="col-12"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                <Link to={`/blog/${blogItem.id}`} className="text-decoration-none">
                    <motion.div
                        className="position-relative rounded-4 overflow-hidden"
                        style={{
                            border: '1px solid rgba(42, 209, 103, 0.15)'
                        }}
                        whileHover={{scale: 1.005}}
                        transition={{duration: 0.4}}
                    >
                        {/* Image with 16:9 aspect ratio */}
                        <div style={{paddingTop: '45%', position: 'relative'}}>
                            <motion.img
                                src={blogItem.imageUrl}
                                alt={blogItem.title}
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{objectFit: 'cover'}}
                                whileHover={{scale: 1.03}}
                                transition={{duration: 0.6}}
                            />
                            {/* Gradient overlay */}
                            <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                    background: 'linear-gradient(to top, rgba(15, 15, 30, 0.95) 0%, rgba(15, 15, 30, 0.7) 40%, transparent 100%)'
                                }}
                            />
                        </div>

                        {/* Content overlay */}
                        <div
                            className="position-absolute bottom-0 start-0 w-100 p-4 p-md-6 p-lg-8"
                        >
                            <div className="row">
                                <div className="col-lg-8">
                                    <div className="mb-3">
                                        <span
                                            className="badge text-uppercase px-3 py-2 me-2"
                                            style={{
                                                background: 'linear-gradient(135deg, #2ad167 0%, #1a9d4a 100%)',
                                                fontSize: '0.7rem'
                                            }}
                                        >
                                            Najnowszy artykuł
                                        </span>
                                        {blogItem.tags.slice(0, 2).map((tag) => (
                                            <span
                                                key={tag}
                                                className="badge text-uppercase px-3 py-2 me-2"
                                                style={{
                                                    background: 'rgba(42, 209, 103, 0.15)',
                                                    color: '#2ad167',
                                                    fontSize: '0.7rem',
                                                    border: '1px solid rgba(42, 209, 103, 0.3)'
                                                }}
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    <h2
                                        className="font-heading text-white mb-3"
                                        style={{
                                            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                                            lineHeight: 1.2,
                                            textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                                        }}
                                    >
                                        {blogItem.title}
                                    </h2>

                                    <p
                                        className="text-secondary-light mb-4 d-none d-md-block"
                                        style={{
                                            fontSize: '1.05rem',
                                            lineHeight: 1.7,
                                            maxWidth: '600px'
                                        }}
                                    >
                                        {blogItem.subtitle}
                                    </p>

                                    <div className="d-flex align-items-center gap-4">
                                        <span
                                            className="btn btn-success px-4 py-2"
                                            style={{
                                                background: 'linear-gradient(135deg, #2ad167 0%, #1a9d4a 100%)',
                                                border: 'none'
                                            }}
                                        >
                                            Przeczytaj artykuł →
                                        </span>
                                        <span className="text-secondary-light small">
                                            {blogItem.author}
                                            {isAdmin && blogItem.addedAt && ` • ${formatISODateString(blogItem.addedAt)}`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Admin controls */}
                        {isAdmin && (
                            <div className="position-absolute top-0 end-0 p-3 d-flex gap-2">
                                <span className="badge bg-dark">
                                    <i className="bi bi-eye me-1"></i>
                                    {blogItem.uniqueViews + blogItem.repeatedViews}
                                </span>
                            </div>
                        )}
                    </motion.div>
                </Link>

                {isAdmin && !readonly && (
                    <div className="d-flex gap-2 mt-3 justify-content-end">
                        <AnimatedLink
                            className="btn btn-sm btn-outline-warning"
                            to={`/admin/blog/edytuj/${blogItem.id}`}
                        >
                            <i className="bi bi-pencil me-1"></i> Edytuj
                        </AnimatedLink>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={(e) => {
                                e.preventDefault();
                                setArticleToDelete(blogItem);
                                setConfirmDeleteModalOpen(true);
                            }}
                        >
                            <i className="bi bi-trash me-1"></i> Usuń
                        </button>
                    </div>
                )}
            </motion.div>
        );
    }

    // Regular card layout
    const colClass = readonly ? 'col-md-12' : (largeCard ? 'col-md-6' : 'col-md-6 col-xl-4');

    return (
        <motion.div
            className={`col-12 ${colClass}`}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div
                className="h-100 rounded-4 overflow-hidden"
                style={{
                    background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.35) 0%, rgba(26, 26, 46, 0.55) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)'
                }}
                whileHover={{
                    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(42, 209, 103, 0.25)',
                    borderColor: 'rgba(42, 209, 103, 0.3)'
                }}
                transition={{duration: 0.2, ease: "easeOut"}}
            >
                {/* Image Container - 16:9 aspect ratio */}
                <Link to={`/blog/${blogItem.id}`} className="d-block position-relative overflow-hidden">
                    <div style={{paddingTop: '56.25%', position: 'relative'}}>
                        <motion.img
                            src={blogItem.imageUrl}
                            alt={blogItem.title}
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{objectFit: 'cover'}}
                            whileHover={{scale: 1.08}}
                            transition={{duration: 0.5}}
                        />
                        {/* Subtle gradient at bottom */}
                        <div
                            className="position-absolute bottom-0 start-0 w-100"
                            style={{
                                height: '40%',
                                background: 'linear-gradient(to top, rgba(26, 26, 46, 0.6) 0%, transparent 100%)'
                            }}
                        />
                    </div>
                </Link>

                {/* Card Body */}
                <div className="p-4 p-lg-5">
                    {/* Tags */}
                    <div className="mb-3 d-flex flex-wrap gap-2">
                        {blogItem.tags.slice(0, 2).map((tag) => (
                            <span
                                key={tag}
                                className="badge text-uppercase"
                                style={{
                                    background: 'rgba(42, 209, 103, 0.12)',
                                    color: '#2ad167',
                                    fontSize: '0.65rem',
                                    padding: '0.45rem 0.75rem',
                                    border: '1px solid rgba(42, 209, 103, 0.25)',
                                    letterSpacing: '0.05em'
                                }}
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* Meta info */}
                    <p className="mb-2 small" style={{color: 'rgba(255,255,255,0.5)'}}>
                        {blogItem.author}
                        {isAdmin && blogItem.addedAt && (
                            <> • {formatISODateString(blogItem.addedAt)}</>
                        )}
                        {isAdmin && (
                            <> • <i className="bi bi-eye"></i> {blogItem.uniqueViews + blogItem.repeatedViews}</>
                        )}
                    </p>

                    {/* Title */}
                    <Link to={`/blog/${blogItem.id}`} className="d-block mb-3 text-decoration-none">
                        <h3
                            className="font-heading text-white mb-0"
                            style={{
                                fontSize: '1.2rem',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                transition: 'color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#2ad167'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'white'}
                        >
                            {blogItem.title}
                        </h3>
                    </Link>

                    {/* Subtitle */}
                    <p
                        className="text-secondary-light mb-4"
                        style={{
                            fontSize: '0.9rem',
                            lineHeight: 1.7,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {blogItem.subtitle}
                    </p>

                    {/* Footer */}
                    <div className="d-flex align-items-center justify-content-between pt-3" style={{borderTop: '1px solid rgba(255,255,255,0.06)'}}>
                        <AnimatedLink
                            className="d-inline-flex align-items-center text-success fw-medium"
                            to={`/blog/${blogItem.id}`}
                            style={{fontSize: '0.9rem'}}
                        >
                            Czytaj więcej
                            <svg
                                className="ms-2"
                                width={18}
                                height={18}
                                viewBox="0 0 24 24"
                                fill="none"
                                style={{transition: 'transform 0.2s'}}
                            >
                                <path
                                    d="M5 12H19M19 12L12 5M19 12L12 19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </AnimatedLink>

                        {isAdmin && !readonly && (
                            <div className="d-flex gap-3">
                                <AnimatedLink
                                    className="text-warning opacity-75"
                                    to={`/admin/blog/edytuj/${blogItem.id}`}
                                    style={{fontSize: '1.1rem'}}
                                >
                                    <i className="bi bi-pencil"></i>
                                </AnimatedLink>
                                <button
                                    className="btn btn-link text-danger p-0 opacity-75"
                                    onClick={() => {
                                        setArticleToDelete(blogItem);
                                        setConfirmDeleteModalOpen(true);
                                    }}
                                    style={{fontSize: '1.1rem'}}
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
